<template>
  <div
    class="newsletter-form"
    :class="[
      $blockClass(blockClass, 'newsletter-form'),
    ]"
  >
    <heading-copy
      :classes="classes"
      :content="{
        headingText: content.title,
        subText: content.copy,
        textColour: content.textColour,
      }"
    />

    <form
      class="form-group"
      :class="[
        formGroupColor,
        {
          [$class('error')]: state === 'error',
          [$class('success')]: state === 'success'
        },
      ]"
    >
      <div class="newsletter-form__row form-group__field">
        <label
          class="form-group__label"
          :class="{ 'visually-hidden': hideLabel }"
          :for="`newsletter-form-email-${namespace}`"
        >
          <span v-text="$string('general.newsletter_form.email.label')" />

          <abbr
            :title="$string('general.required.label')"
            v-text="$string('general.required.symbol')"
          />
        </label>

        <input
          :id="`newsletter-form-email-${namespace}`"
          ref="email"
          v-model="email"
          :aria-describedby="getAriaDescribedby('email')"
          :aria-invalid="getAriaInvalid('email')"
          autocomplete="email"
          class="form-group__input text-body-m-regular"
          :disabled="state === 'success'"
          name="email"
          :placeholder="emailPlaceholder"
          required
          type="email"
        >

        <btn
          :disabled="state === 'success'"
          :label="$string('general.newsletter_form.submit')"
          modifiers="center fill primary"
          type="submit"
          @click.prevent="handleFormSubmit"
        />

        <div
          :id="`newsletter-status-${namespace}`"
          class="form-group__note"
          role="status"
        >
          <p
            class="text-body-xs-regular"
            v-text="statusMessage"
          />
        </div>
      </div>
    </form>
  </div>
</template>

<script>

/**
 * Vue: Newsletter form (newsletter-form)
 * -----------------------------------------------------------------------------
 * Snippet containing a simple email newsletter subscription form and associated
 * messaging.
 *
 * @param {String} [blockClass] - Add block class for BEM.
 * @param {Object} [classes] - Object containing text classes.
 * @param {String} [classes.copy] - Text classes for copy.
 * @param {String} [classes.heading] - Text classes for heading.
 * @param {String} [colorMode] - Dark/light mode.
 * @param {String} [content] - Additional text for form.
 * @param {String} [content.copy] - Description text.
 * @param {String} [content.title] - Heading text.
 * @param {String} namespace - Unique namespace.
 *
 */
import { mapState } from 'vuex'
import { isEmailValid } from '~/helpers/general'
import { trackBloomreachEvent } from '~/helpers/bloomreach'

import Btn from '~global/btn/btn'
import HeadingCopy from '~async/heading-copy/heading-copy'

export default {
  name: 'NewsletterForm',

  components: {
    Btn,
    HeadingCopy,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    classes: {
      type: Object,
      default: () => ({
        copy: 'text-body-s-regular text-body-m-regular-desktop',
        heading: 'text-heading-xl text-heading-l-desktop',
      }),
    },
    colorMode: {
      type: String,
      default: 'light',
      validator: (value) => {
        return ['dark', 'light'].includes(value)
      },
    },
    content: {
      type: Object,
      default: () => ({
        copy: '',
        title: '',
        textColour: '',
      }),
    },
    hideLabel: {
      type: Boolean,
      default: false,
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
  },

  data() {
    return {
      email: '',
      errorMessages: {
        email: '',
        generic: '',
      },
      hasSubmitted: false,
      state: 'ready',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Return email placeholder.
     * @returns {String}
     */
    emailPlaceholder() {
      return this.isMobile
        ? this.$string('general.newsletter_form.email.placeholder_mobile')
        : this.$string('general.newsletter_form.email.placeholder')
    },

    /**
     * Return form group colour modifier class.
     * @returns {String}
     */
    formGroupColor() {
      return `form-group--${this.colorMode}`
    },

    /**
     * Return form status message.
     * @returns {String}
     */
    statusMessage() {
      if (this.state === 'ready') {
        return ''
      }

      if (this.state === 'success') {
        return this.$string('general.newsletter_form.success')
      }

      return this.errorMessages.email
        ? this.errorMessages.email
        : this.errorMessages.generic
    },
  },

  methods: {

    /**
     * Return the id of the error message to populate the appropriate
     * `aria-describedby` attribute.
     * - Set to `null` by default, and if no error message is present to prevent
     *   the attribute from appearing and pointing to a non-existant element.
     * @param {String} field - Type of input field. Accepts 'email'.
     * @returns {String|null}
     */
    getAriaDescribedby(field) {
      if (!this.hasSubmitted) {
        return null
      }

      let ariaDescribedby = null

      switch (field) {
        case 'email':
          ariaDescribedby = isEmailValid(this.email)
            ? null
            : `newsletter-status-${this.namespace}`
          break
      }

      return ariaDescribedby
    },

    /**
     * Set `aria-invalid` attribute.
     * - Set to `null` by default to prevent the attribute from appearing on
     *   the element (mirrors similar Liquid implementations).
     * @param {String} field - Type of input field. Accepts 'email'.
     * @returns {Boolean|null}
     */
    getAriaInvalid(field) {
      if (!this.hasSubmitted) {
        return null
      }

      let isInvalid = null

      switch (field) {
        case 'email':
          isInvalid = !isEmailValid(this.email)
          break
      }

      return isInvalid
    },

    /**
     * Handle events on newsletter form submission.
     * - Prevent form from submitting if any errors are present, and set error
     *   messages.
     */
    handleFormSubmit() {
      this.hasSubmitted = true
      this.resetErrorMessages()
      this.state = 'ready'

      this.email = this.email.trim()

      if (!isEmailValid(this.email)) {
        this.state = 'error'
        this.errorMessages.email = this.$string('general.newsletter_form.email.error')

        this.$refs.email.focus()
      }

      if (this.state === 'error') {
        this.errorMessages.generic = this.$string('general.newsletter_form.error_generic')
        return
      }

      /**
       * Store the email in sessionStorage if the user is not logged in.
       */
      if (!this.$variable('customer.loggedIn')) {
        sessionStorage.setItem('userEmail', this.email)
      }

      // eslint-disable-next-line no-unused-vars
      const subscribe = new Promise((resolve, reject) => {
        resolve(
          trackBloomreachEvent('newsletter_consent', { email: this.email }),
        )
      })

      subscribe
        .then(() => {
          this.state = 'success'
        })
        .catch(() => {
          this.state = 'error'
        })
    },

    /**
     * Reset error messages to default (blank string).
     */
    resetErrorMessages() {
      this.errorMessages.email = ''
      this.errorMessages.generic = ''
    },
  },
}
</script>

<style lang="scss">
@import './newsletter-form';
</style>
